import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";

import { appStore } from "@store/appStore";

import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import showAlert from "src/shared/utils/generic.tsx";

import { CommandGetOpsSummary } from "./commands/cmdGetOpsSummary";
import { FilterSelect } from "./components/FilterSelect";
import { OpsTileCard } from "./components/OpsTileCard";
import styles from "./opsDashboard.module.css";
import { DATE_WINDOWS, TAB_GROUPS } from "./opsDashboard.types";
import { toggleInTree } from "./orgTree";
import { MarginTracking } from "./components/MarginTracking";
import type {
    OpsFilterOption, OpsFilters, OpsSummary, OpsTab, TileGroup,
} from "./opsDashboard.types";

const TABS: { key: OpsTab; label: string }[] = [
    { key: "onboarding", label: "Onboarding" },
    { key: "live", label: "Live" },
    { key: "ended", label: "Ended" },
    // Last, and after Ended rather than beside the other three: the first three are one
    // sequence a campaign moves along, and this one is not a stop on it (MPN-1868).
    { key: "client", label: "Client Management" },
];

/** The mockup persists open/closed groups under exactly this key. */
const GROUP_STORE = "ops-dash-groups-v1";

/** Filter selections, kept between visits per MPN-1838. */
const FILTER_STORE = "ops-dash-filters-v1";

interface SavedFilters {
    opsOwnerIds?: number[];
    organizationIds?: number[];
    csmIds?: number[];
    contractIds?: number[];
    startWindow?: string;
    endWindow?: string;
}

function loadFilters(): SavedFilters {
    try {
        return JSON.parse(window.localStorage.getItem(FILTER_STORE) || "{}") || {};
    } catch {
        return {};
    }
}

/** Empty-state copy, verbatim from the mockup where it gives one. */
const EMPTY_TITLES: Record<string, string> = {
    overdueFiles: "Nothing overdue for this filter.",
    adsScheduling: "No gaps in the next 7 days.",
    audienceChanges: "No unusual conversion activity.",
    conversionsFlat: "Conversions moving on every campaign.",
    constituents: "No files processed for this filter.",
    offboard: "Nothing offboarding in the next 14 days.",
    noContinuation: "No campaigns ending without continuation.",
};

/**
 * Every collapsible group starts closed.
 *
 * List Size used to be defaulted open here. It is `fixed` now — MPN-1839 puts it with
 * Triage as a section that is ALWAYS expanded — so a default would be describing a
 * control that no longer exists.
 */
const DEFAULT_OPEN_GROUPS: Record<string, boolean> = {};

function loadOpenGroups(): Record<string, boolean> {
    try {
        const saved = JSON.parse(window.localStorage.getItem(GROUP_STORE) || "null");
        return { ...DEFAULT_OPEN_GROUPS, ...(saved || {}) };
    } catch {
        return { ...DEFAULT_OPEN_GROUPS };
    }
}

export default function OpsDashboardPage() {
    useDocumentTitle("Operations Dashboard");
    const appSnap = useSnapshot(appStore);

    const saved = useRef(loadFilters()).current;

    const [tab, setTab] = useState<OpsTab>("live");
    const [opsOwnerIds, setOpsOwnerIds] = useState<number[]>(saved.opsOwnerIds ?? []);
    const [organizationIds, setOrganizationIds] = useState<number[]>(saved.organizationIds ?? []);
    const [csmIds, setCsmIds] = useState<number[]>(saved.csmIds ?? []);
    const [contractIds, setContractIds] = useState<number[]>(saved.contractIds ?? []);
    const [startWindow, setStartWindow] = useState<string>(saved.startWindow ?? "");
    const [endWindow, setEndWindow] = useState<string>(saved.endWindow ?? "");
    /**
     * Whether the Ops Owner default has been applied. The ticket asks for the filter to
     * start on the signed-in user, but only when they have not chosen for themselves —
     * so it runs once, and never over a restored selection or a deliberate "All".
     */
    const defaulted = useRef(saved.opsOwnerIds !== undefined);
    const [openFilter, setOpenFilter] = useState<string | null>(null);
    const [summary, setSummary] = useState<OpsSummary | null>(null);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);
    const [activeJump, setActiveJump] = useState<string | null>(null);
    // Set while a jump-bar click is scrolling, so the scrollspy below leaves the
    // highlight alone until the page arrives.
    const jumping = useRef(false);
    const jumpRelease = useRef<number | undefined>(undefined);

    const filters: OpsFilters = useMemo(
        () => ({ tab, opsOwnerIds, organizationIds, csmIds, contractIds, startWindow, endWindow }),
        [tab, opsOwnerIds, organizationIds, csmIds, contractIds, startWindow, endWindow]
    );

    // Persist every selection but the tab: the tab is where you are, not what you filtered.
    useEffect(() => {
        try {
            window.localStorage.setItem(FILTER_STORE, JSON.stringify({
                opsOwnerIds, organizationIds, csmIds, contractIds, startWindow, endWindow,
            }));
        } catch {
            /* private browsing — the page works, it just forgets */
        }
    }, [opsOwnerIds, organizationIds, csmIds, contractIds, startWindow, endWindow]);

    /**
     * Default the Ops Owner filter to the signed-in user (MPN-1838).
     *
     * Waits for the summary, because the options are built from the rows on the page and
     * the user is only offered when they actually own something today. Applied once, and
     * skipped entirely for someone who has been here before — otherwise clearing the
     * filter would re-apply it on the next visit and look broken.
     */
    useEffect(() => {
        if (defaulted.current || !summary) return;
        defaulted.current = true;
        const mine = appSnap.loggedInUser?.employee?.id;
        if (mine && summary.ops_owners.some((o) => o.id === mine)) setOpsOwnerIds([mine]);
    }, [summary, appSnap.loggedInUser?.employee?.id]);

    useEffect(() => {
        let cancelled = false;
        new CommandGetOpsSummary()
            .run(filters)
            .then((r) => {
                if (!cancelled) setSummary(r);
            })
            .catch(() => {
                // A 403 here is the common case — the route itself is not permission
                // guarded, so anyone without Platform Admin lands on it and the API
                // refuses. Without this the page sits on "Loading…" indefinitely.
                if (!cancelled) showAlert("Failed to load the operations dashboard", "error", 4000);
            });
        return () => {
            cancelled = true;
        };
    }, [filters]);

    const toggleGroup = useCallback((key: string) => {
        setOpenGroups((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            try {
                window.localStorage.setItem(GROUP_STORE, JSON.stringify(next));
            } catch {
                /* private browsing — the page works, it just forgets */
            }
            return next;
        });
    }, []);

    const counts = useMemo(() => {
        const map: Record<string, number> = {};
        summary?.tiles.forEach((t) => {
            map[t.tile_key] = t.count;
        });
        return map;
    }, [summary]);

    /**
     * Which tiles actually have a rule behind them.
     *
     * The summary lists one entry per REGISTERED backend tile, so it doubles as the
     * source of truth for what exists. Rendering only these means a table can never sit
     * permanently empty because its rule was never written — it simply does not appear,
     * and appears the moment the rule lands. A healthy-but-empty table still renders its
     * empty state, because its key IS in the summary with a count of 0.
     *
     * Null until the summary resolves, which is how the first paint avoids flashing every
     * card and then removing some.
     */
    /** tile_key -> what is blocking it, where the backend reports something. */
    /** tile_key -> its rule as maths, and the caveat under it. */
    const calculations = useMemo(() => {
        const map: Record<string, { formula: string; method: string; tiers: string[][] }> = {};
        summary?.tiles.forEach((t) => {
            if (t.formula || t.method || t.tiers?.length) {
                map[t.tile_key] = { formula: t.formula, method: t.method, tiers: t.tiers ?? [] };
            }
        });
        return map;
    }, [summary]);

    /** The tiles whose rows Ops can accept as fine (MPN-2010). */
    const acceptable = useMemo(
        () => new Set(summary?.tiles.filter((t) => t.acceptable).map((t) => t.tile_key) ?? []),
        [summary]
    );

    const implemented = useMemo(
        () => (summary ? new Set(summary.tiles.map((t) => t.tile_key)) : null),
        [summary]
    );

    /** A group's tiles, narrowed to those the backend actually implements. */
    const liveTiles = (tiles: string[]) =>
        implemented ? tiles.filter((k) => implemented.has(k)) : [];

    const groupCount = (tiles: string[]) =>
        liveTiles(tiles).reduce((s, k) => s + (counts[k] ?? 0), 0);

    // Groups whose every tile is unimplemented are dropped entirely — otherwise the page
    // shows a heading and a jump-bar pill leading to nothing.
    const groups = TAB_GROUPS[tab].filter((g) => liveTiles(g.tiles).length > 0);

    /**
     * Tabs with at least one tile on them. The dashboard ships incrementally, so a whole
     * tab can be held back at once — Ended was, until MPN-1867 landed its only table. Its
     * pill would otherwise carry a row count from `tab_totals`, which the backend computes
     * across every registered rule, and lead to a blank page.
     *
     * Empty until the summary resolves, because only the summary says which rules exist.
     * That leaves the pill row out of the first paint rather than rendering every pill and
     * then removing one, and the header already reads "Loading…" beside it.
     */
    const visibleTabs = implemented
        ? TABS.filter((t) =>
            TAB_GROUPS[t.key].some((g) => g.tiles.some((k) => implemented.has(k))))
        : [];

    /**
     * Toggling respects the option list's hierarchy: ticking a parent organization ticks
     * the campuses beneath it, so the checkboxes show what the filter will actually
     * match. Flat lists have no relations, so Ops Owner and CSM are unaffected.
     */
    const toggle =
        (setter: React.Dispatch<React.SetStateAction<number[]>>, options: OpsFilterOption[]) =>
        (id: number) =>
            setter((prev) => toggleInTree(options, prev, id));

    const dateWindow = DATE_WINDOWS[tab];

    const picked =
        opsOwnerIds.length + organizationIds.length + csmIds.length + contractIds.length
        + (startWindow ? 1 : 0) + (endWindow ? 1 : 0);


    /**
     * When the page last fetched, which is the only staleness signal there is: nothing
     * auto-refreshes, so a tab left open overnight shows yesterday's numbers — which is
     * exactly why the date is here and not only the time.
     *
     * What went is the mockup's second line, "Refreshes hourly". That described the
     * snapshot table that was dropped; with every figure computed on request there is no
     * refresh cycle to describe.
     */
    const stamp = summary?.generated_at
        ? new Date(summary.generated_at).toLocaleString(undefined, {
              month: "short", day: "numeric", year: "numeric",
              hour: "numeric", minute: "2-digit",
          })
        : null;

    /**
     * Filter options are built from the rows in the CURRENT tab, so a selection made on
     * one tab may not exist on the next. Carrying it over silently empties every table
     * while the filter bar reads "All Organizations" — the selected id no longer matches
     * an option, so its label falls back. Clearing on switch keeps the bar truthful.
     */
    const switchTab = (next: OpsTab) => {
        if (next === tab) return;
        setOpsOwnerIds([]);
        setOrganizationIds([]);
        setCsmIds([]);
        setOpenFilter(null);
        setTab(next);
    };

    /**
     * Highlight whichever section is currently in view (MPN-1839).
     *
     * IntersectionObserver rather than a scroll listener: it fires only when a heading
     * actually crosses the line, so scrolling costs nothing, and the browser does the
     * geometry. The root margin pins the trigger just under the sticky jump bar — the
     * section you are "in" is the one whose heading has passed beneath it, not one still
     * below the fold.
     *
     * Re-runs when the groups change, because a tab switch replaces every heading.
     */
    useEffect(() => {
        if (!groups.length) return;
        const headings = groups
            .map((g) => document.getElementById(g.id))
            .filter((el): el is HTMLElement => el !== null);
        if (!headings.length) return;

        const seen = new Map<string, boolean>();
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
                // A click already named the destination, and the smooth scroll on its way
                // there crosses every heading in between -- each one firing this callback.
                // Acting on those walked the highlight backwards through the sections the
                // page was passing over before it settled on the one that was clicked.
                if (jumping.current) return;
                // The topmost heading currently past the bar wins, so scrolling down
                // hands off cleanly rather than flickering between two neighbours.
                const current = headings.find((el) => seen.get(el.id));
                if (current) setActiveJump(current.id);
            },
            { rootMargin: "-64px 0px -70% 0px", threshold: 0 }
        );
        headings.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [groups]);

    const jumpTo = (group: TileGroup) => {
        if (!group.fixed && !openGroups[group.key]) toggleGroup(group.key);
        setActiveJump(group.id);

        // Held until the scroll settles. `scrollend` is the real signal; the timeout is
        // the backstop for browsers that do not fire it and for a scroll that never
        // starts because the section is already in view.
        jumping.current = true;
        window.clearTimeout(jumpRelease.current);
        const release = () => {
            jumping.current = false;
            window.clearTimeout(jumpRelease.current);
            document.removeEventListener("scrollend", release);
        };
        document.addEventListener("scrollend", release, { once: true });
        jumpRelease.current = window.setTimeout(release, 1200);

        requestAnimationFrame(() => {
            document.getElementById(group.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <h1 className={styles.title}>Operations Dashboard</h1>
                <div className={styles.pills}>
                    {visibleTabs.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.key}
                            className={`${styles.pill} ${tab === t.key ? styles.pillOn : ""}`}
                            onClick={() => switchTab(t.key)}
                        >
                            {t.label}
                            <span className={styles.pillChip}>
                                {summary?.tab_totals?.[t.key] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>
                <div className={styles.stamp}>
                    {stamp ? `Data as of ${stamp}` : "Loading…"}
                </div>
            </div>

            <div className={styles.filters}>
                <FilterSelect
                    label="Ops Owner"
                    allLabel="All Ops Owners"
                    options={summary?.ops_owners ?? []}
                    selected={opsOwnerIds}
                    isOpen={openFilter === "owner"}
                    onToggleOpen={() => setOpenFilter(openFilter === "owner" ? null : "owner")}
                    onToggleOption={toggle(setOpsOwnerIds, summary?.ops_owners ?? [])}
                />
                <FilterSelect
                    label="Organization"
                    allLabel="All Organizations"
                    options={summary?.organizations ?? []}
                    selected={organizationIds}
                    isOpen={openFilter === "org"}
                    onToggleOpen={() => setOpenFilter(openFilter === "org" ? null : "org")}
                    onToggleOption={toggle(setOrganizationIds, summary?.organizations ?? [])}
                />
                <FilterSelect
                    label="Contract"
                    allLabel="All Contracts"
                    options={summary?.contracts ?? []}
                    selected={contractIds}
                    isOpen={openFilter === "contract"}
                    onToggleOpen={() => setOpenFilter(openFilter === "contract" ? null : "contract")}
                    onToggleOption={toggle(setContractIds, summary?.contracts ?? [])}
                />
                <FilterSelect
                    label="CSM"
                    allLabel="All CSMs"
                    options={summary?.csms ?? []}
                    selected={csmIds}
                    isOpen={openFilter === "csm"}
                    onToggleOpen={() => setOpenFilter(openFilter === "csm" ? null : "csm")}
                    onToggleOption={toggle(setCsmIds, summary?.csms ?? [])}
                />
                <button
                    type="button"
                    disabled={picked === 0}
                    className={`${styles.reset} ${picked ? styles.resetOn : ""}`}
                    onClick={() => {
                        setOpsOwnerIds([]);
                        setOrganizationIds([]);
                        setCsmIds([]);
                        setContractIds([]);
                        setStartWindow("");
                        setEndWindow("");
                        setOpenFilter(null);
                    }}
                >
                    Reset filters
                </button>
            </div>


            {/* Its own row beneath the filter card, per the design: a segmented control
                rather than a dropdown, because the options are few, exhaustive and worth
                seeing at a glance on a bar that already hides four multi-selects. */}
            {dateWindow && (
                <div className={styles.windowRow}>
                    <span className={styles.windowLabel}>{dateWindow.label}</span>
                    <div className={styles.windowSeg} role="group" aria-label={dateWindow.label}>
                        {[{ value: "", label: dateWindow.allLabel }, ...dateWindow.options].map((o) => {
                            const active = (tab === "onboarding" ? startWindow : endWindow) === o.value;
                            return (
                                <button
                                    key={o.value || "all"}
                                    type="button"
                                    aria-pressed={active}
                                    className={`${styles.windowBtn} ${active ? styles.windowBtnOn : ""}`}
                                    onClick={() => (tab === "onboarding"
                                        ? setStartWindow(o.value)
                                        : setEndWindow(o.value))}
                                >
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                    <span className={styles.windowHelp}>{dateWindow.help}</span>
                </div>
            )}

            {/* A one-section tab has nothing to jump between, and the single pill reads as a
                duplicate of the tab it sits under — on Client Management it carries the
                same label and the same count. Ended is the other one-section tab. */}
            {groups.length > 1 && (
            <div className={styles.jump}>
                {groups.map((g) => (
                    <button
                        key={g.key}
                        type="button"
                        className={`${styles.jumpItem} ${activeJump === g.id ? styles.jumpItemOn : ""}`}
                        onClick={() => jumpTo(g)}
                    >
                        <span className={styles.jumpLabel} data-label={g.label}>
                            {g.label}
                        </span>
                        <span className={styles.jumpCount}>{groupCount(g.tiles)}</span>
                    </button>
                ))}
            </div>
            )}

            {groups.map((g) => {
                const n = groupCount(g.tiles);
                const isOpen = g.fixed || !!openGroups[g.key];
                const countText = `${n} ${n === 1 ? "Item" : "Items"}`;

                // A panel groups two halves of one question inside a single container;
                // a section divides the page. Different things, drawn differently.
                if (g.panel) {
                    return (
                        <div key={g.key} id={g.id} className={styles.panel}>
                            <h2 className={styles.panelTitle}>{g.heading ?? g.label}</h2>
                            {g.description && (
                                <p className={styles.panelDesc}>{g.description}</p>
                            )}
                            <div className={g.layout === "grid" ? styles.grid : styles.single}>
                                {liveTiles(g.tiles).map((tileKey) => (
                                    <OpsTileCard
                                        key={tileKey}
                                        tileKey={tileKey}
                                        count={counts[tileKey] ?? 0}
                                        filters={filters}
                                        emptyTitle={EMPTY_TITLES[tileKey] ?? "Nothing flagged here."}
                                        formula={calculations[tileKey]?.formula}
                                        method={calculations[tileKey]?.method}
                                        tiers={calculations[tileKey]?.tiers}
                                        acceptable={acceptable.has(tileKey)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={g.key}>
                        {g.fixed ? (
                            <div id={g.id} className={styles.groupHeadFixed}>
                                <div className={styles.groupHeadRow}>
                                    <h2 className={styles.groupTitle}>{g.heading ?? g.label}</h2>
                                    <span className={styles.groupCount}>{countText}</span>
                                </div>
                                {g.description && <p className={styles.groupDesc}>{g.description}</p>}
                            </div>
                        ) : (
                            <button
                                type="button"
                                id={g.id}
                                className={styles.groupHead}
                                aria-expanded={isOpen}
                                onClick={() => toggleGroup(g.key)}
                            >
                                <span className={styles.chev}>{isOpen ? "▾" : "▸"}</span>
                                <h2 className={styles.groupTitle}>{g.heading ?? g.label}</h2>
                                <span className={styles.groupCount}>{countText}</span>
                            </button>
                        )}

                        {isOpen && !g.fixed && g.description && (
                            <p className={styles.tileNote}>{g.description}</p>
                        )}

                        {isOpen && g.lead === "marginTracking" && <MarginTracking />}
                        {isOpen && (
                            <div className={g.layout === "grid" ? styles.grid : styles.single}>
                                {liveTiles(g.tiles).map((tileKey) => (
                                    <OpsTileCard
                                        key={tileKey}
                                        tileKey={tileKey}
                                        count={counts[tileKey] ?? 0}
                                        filters={filters}
                                        emptyTitle={EMPTY_TITLES[tileKey] ?? "Nothing flagged here."}
                                        formula={calculations[tileKey]?.formula}
                                        method={calculations[tileKey]?.method}
                                        tiers={calculations[tileKey]?.tiers}
                                        acceptable={acceptable.has(tileKey)}
                                    />
                                ))}
                                {/* Reporting that is coming but not yet specified
                                    (MPN-1867). A dashed slot rather than a card: an
                                    empty card reads as a table that failed to load. */}
                                {g.placeholder && (
                                    <div className={styles.placeholder}>
                                        <span className={styles.placeholderLabel}>Still to come</span>
                                        <span>{g.placeholder}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
